<template>
  <div class="container">
    <div class="my-4">
      <ProgressNav>
        <ProgressNavList>
          <ProgressNavItem
            available
            class="mx-1"
            @click="navigateToFlowSelection"
          >
            {{ $t("signup.fi.flow_selection.choose_onboarding") }}
          </ProgressNavItem>
          <ProgressNavItem
            v-for="step in fiStore.enabledSteps"
            :key="step.id"
            :active="fiStore.currentStep?.id === step.id"
            :available="fiStore.isStepAccessible(step.id)"
            :disabled="!fiStore.isStepAccessible(step.id)"
            class="mx-1"
            @click="handleStepClick(step.id)"
          >
            {{ step.title }}
          </ProgressNavItem>
        </ProgressNavList>
      </ProgressNav>
    </div>

    <!-- Step content -->
    <div class="step-content">
      <component
        :is="fiStore.currentStep?.component"
        v-if="fiStore.currentStep?.component && loaded"
      />

      <LoadingOverlayComponent v-else />
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import { useAccount } from "@/composables/account/useAccount";
import { useFISignupStore } from "@/signup/fiSignupStore";
import {
  ProgressNav,
  ProgressNavItem,
  ProgressNavList,
} from "@/signup/progress-nav";
import { useSignupStore } from "@/signup/signup";
import { useRouteParams } from "@vueuse/router";
import { onMounted, ref, watch } from "vue";
import { onBeforeRouteLeave, useRouter } from "vue-router";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const router = useRouter();
const fiStore = useFISignupStore();
const signupStore = useSignupStore();

// TODO: Hardcoded to FI until the fallback flow is cleaned up and made to use
// same code as the FI flow
const country = ref<string>("FI");

const { globalErrorHandler } = useGlobalHandlers();

// Get the current flow from route params
const currentFlow = useRouteParams("flow");

// Handle clicking on a step in the navigation
const handleStepClick = async (stepId: string) => {
  if (fiStore.isStepAccessible(stepId)) {
    // A step forward saves the current one, which can fail like the Next button's save.
    await fiStore.goto(stepId).catch(globalErrorHandler);
  }
};

const navigateToFlowSelection = () => {
  const countryCode =
    typeof country.value === "string" ? country.value.toLowerCase() : undefined;

  if (countryCode) {
    router.push({
      name: "FISignupFlowSelection",
      query: { country: countryCode },
    });
  } else {
    router.push({
      name: "ProducerSignUp",
    });
  }
};

// Fetch account data first, then initialize both stores
const { loaded } = useAccount((accountData) => {
  if (typeof country.value !== "string") {
    router.push({
      name: "ProducerSignUp",
    });
    return;
  }

  try {
    // Initialize the useSignupStore with account data
    signupStore.initialize({
      initialAccount: accountData,
      defaultCountry: country.value,
    });

    signupStore.createCompanyIfMissing();

    // Initialize just the navigation flow in fiStore
    fiStore.initialize(currentFlow.value as string);
  } catch (error) {
    console.error(error);
    globalErrorHandler(error as Error);
  }
});

// Initialize the store with the current flow from route
onMounted(() => {
  if (!currentFlow.value) {
    router.push({
      name: "FISignupFlowSelection",
      query: {
        country: country.value,
      },
    });
    return;
  }
});

// Watch for flow changes in the route
watch(
  () => currentFlow.value,
  (newFlow) => {
    if (newFlow && newFlow !== fiStore.currentFlowId) {
      fiStore.initialize(newFlow as string);
    }
  }
);

// Dispose so the next time we enter the signup, we need to choose the company again
onBeforeRouteLeave(() => {
  signupStore.$dispose();
  fiStore.$dispose();
});
</script>
